/**
 * Incoming webhooks for one room (section 19).
 *
 * The secret is the whole point of care here: the server keeps only a hash of
 * it, so the reply to creating or rotating one is the single moment it will
 * ever be readable. That moment gets a modal of its own, a copy button, and a
 * warning that does not scroll away — losing it means rotating, which breaks
 * whatever was already posting.
 */

import { useMemo, useState } from 'react'
import type { ChangeEvent, ReactElement, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { isApiError } from '@/api/client'
import {
  queryKeys,
  uploadWebhookAvatar,
  useCreateWebhook,
  useDeleteWebhook,
  useDeleteWebhookAvatar,
  useRotateWebhook,
  useSetWebhookAvatarUrl,
  useUpdateWebhook,
  useWebhooks,
} from '@/api/queries'
import type { Room, Webhook, WebhookSecret } from '@/api/types'
import { BotAvatarField } from '@/components/BotAvatarField'
import { Avatar, Badge, Button, Checkbox, IconButton, Modal, Select, Spinner, TextField, useToast } from '@/components/ui'
import { WebhookMessageBuilder } from '@/components/WebhookMessageBuilder'
import { formatRelative } from '@/lib/format'
import { externalImageSrc, useExternalImages } from '@/lib/externalImages'
import styles from './RoomWebhooks.module.css'

const RATE_CHOICES = [10, 30, 60, 120, 300] as const
const DEFAULT_RATE = 60

function describe(error: unknown, fallback: string): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message !== '') return error.message
  return fallback
}

/** The server's own default may sit outside the presets, so it is folded in. */
function rateOptions(current: number): Array<{ value: string; label: string }> {
  const values = new Set<number>(RATE_CHOICES)
  values.add(current)
  return [...values]
    .sort((left, right) => left - right)
    .map((value) => ({ value: String(value), label: `${value} per minute` }))
}

function curlExample(url: string): string {
  return `curl -X POST '${url}' -H 'Content-Type: application/json' -d '{"text":"Build 412 passed","sender":"CI"}'`
}

async function copyText(text: string): Promise<boolean> {
  const clipboard: Clipboard | undefined = navigator.clipboard

  if (clipboard !== undefined) {
    try {
      await clipboard.writeText(text)
      return true
    } catch {
      // A page served over plain HTTP on a LAN address has no clipboard API.
      // The textarea below still works there.
    }
  }

  try {
    const area = document.createElement('textarea')
    area.value = text
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.top = '-1000px'
    document.body.appendChild(area)
    area.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(area)
    return copied
  } catch {
    return false
  }
}

// ── Copyable block ──────────────────────────────────────────────────────────

interface CopyBlockProps {
  label: string
  value: string
  hint?: ReactNode
}

function CopyBlock({ label, value, hint }: CopyBlockProps): ReactElement {
  const [copied, setCopied] = useState(false)
  const toast = useToast()

  const onCopy = (): void => {
    void copyText(value).then((ok) => {
      if (!ok) {
        toast.error('Your browser would not let the page copy. Select the text and copy it by hand.')
        return
      }
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className={styles.copyBlock}>
      <div className={styles.copyHeader}>
        <span className={styles.sectionTitle}>{label}</span>
        <Button size="sm" variant="secondary" onClick={onCopy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <code className={styles.code}>{value}</code>
      {hint !== undefined ? <p className={styles.note}>{hint}</p> : null}
      <span className="sr-only" role="status">
        {copied ? `${label} copied to the clipboard` : ''}
      </span>
    </div>
  )
}

// ── One webhook ─────────────────────────────────────────────────────────────

interface WebhookRowProps {
  webhook: Webhook
  roomId: string
  onRotate: (webhook: Webhook) => void
  onDelete: (webhook: Webhook) => void
}

function WebhookRow({ webhook, roomId, onRotate, onDelete }: WebhookRowProps): ReactElement {
  const toast = useToast()
  const queryClient = useQueryClient()
  const updateWebhook = useUpdateWebhook()
  const deleteWebhookAvatar = useDeleteWebhookAvatar()
  const setWebhookAvatarUrl = useSetWebhookAvatarUrl()
  const allowExternalImages = useExternalImages()
  const [editingAvatar, setEditingAvatar] = useState(false)
  const options = useMemo(() => rateOptions(webhook.rate_limit_per_minute), [webhook.rate_limit_per_minute])

  const change = (changes: { enabled?: boolean; rate_limit_per_minute?: number }): void => {
    updateWebhook.mutate(
      { roomId, webhookId: webhook.id, changes },
      {
        onError: (error) => toast.error(describe(error, `Could not update ${webhook.name}.`)),
      },
    )
  }

  // Uploading is not a mutation hook — see the note on `uploadBotAvatar` in
  // queries.ts — so this invalidates the room's webhook list itself, the same
  // way the delete and set-URL mutations below do from their own `onSuccess`.
  const uploadAvatar = async (file: File, onProgress: (fraction: number) => void): Promise<string | null> => {
    const result = await uploadWebhookAvatar({ roomId, webhookId: webhook.id }, file, onProgress)
    void queryClient.invalidateQueries({ queryKey: queryKeys.room.webhooks(roomId) })
    return result.avatar_url
  }

  const removeAvatar = async (): Promise<string | null> => {
    const result = await deleteWebhookAvatar.mutateAsync({ roomId, webhookId: webhook.id })
    return result.avatar_url
  }

  const setUrlAvatar = async (url: string): Promise<string | null> => {
    const result = await setWebhookAvatarUrl.mutateAsync({ roomId, webhookId: webhook.id, url })
    return result.avatar_url
  }

  const lastUsed =
    webhook.last_used_at === null ? 'Never used' : `Last used ${formatRelative(webhook.last_used_at)}`

  return (
    <li className={styles.webhook}>
      <div className={styles.webhookTop}>
        <span className={styles.rowName}>
          <Avatar
            name={webhook.name}
            id={webhook.id}
            src={externalImageSrc(webhook.bot_avatar_url, allowExternalImages)}
            size="sm"
            decorative
          />
          <span className={styles.rowNameText}>{webhook.name}</span>
          {webhook.enabled ? null : <Badge tone="neutral">Disabled</Badge>}
        </span>
        <div className={styles.rowActions}>
          <IconButton
            label={`Edit the avatar for ${webhook.name}`}
            size="sm"
            variant="ghost"
            pressed={editingAvatar}
            onClick={() => setEditingAvatar((value) => !value)}
          >
            <ImageIcon />
          </IconButton>
          <IconButton
            label={`Rotate the secret for ${webhook.name}`}
            size="sm"
            variant="ghost"
            onClick={() => onRotate(webhook)}
          >
            <RotateIcon />
          </IconButton>
          <IconButton
            label={`Revoke ${webhook.name}`}
            size="sm"
            variant="ghost"
            onClick={() => onDelete(webhook)}
          >
            <TrashIcon />
          </IconButton>
        </div>
      </div>

      <p className={styles.rowMeta}>{`${webhook.secret_prefix}… · ${lastUsed}`}</p>

      {editingAvatar ? (
        <BotAvatarField
          name={webhook.name}
          currentUrl={webhook.bot_avatar_url}
          onUpload={uploadAvatar}
          onRemove={removeAvatar}
          onSetUrl={setUrlAvatar}
        />
      ) : null}

      <div className={styles.webhookControls}>
        <Checkbox
          checked={webhook.enabled}
          disabled={updateWebhook.isPending}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            change({ enabled: event.target.checked })
          }
          label={
            <>
              Enabled
              <span className="sr-only">{` for ${webhook.name}`}</span>
            </>
          }
        />
        <Select
          label={`Rate limit for ${webhook.name}`}
          labelHidden
          className={styles.rateSelect}
          value={String(webhook.rate_limit_per_minute)}
          disabled={updateWebhook.isPending}
          options={options}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => {
            const parsed = Number.parseInt(event.target.value, 10)
            if (Number.isFinite(parsed)) change({ rate_limit_per_minute: parsed })
          }}
        />
      </div>
    </li>
  )
}

// ── Icons ───────────────────────────────────────────────────────────────────

function ImageIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="5.5" cy="6.5" r="1.1" fill="currentColor" />
      <path
        d="m3 11.5 3.2-3.2a1 1 0 0 1 1.4 0L10 10.7m1-1.7 1-1a1 1 0 0 1 1.4 0L14 9.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function RotateIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M13 8a5 5 0 1 1-1.6-3.7M13 2.5V5h-2.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function TrashIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M3.5 4.5h9m-7 0v-1a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1m-6 0 .6 8a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// ── The manager ─────────────────────────────────────────────────────────────

export interface RoomWebhooksProps {
  room: Room
  /** Fits the manager into the channel details column and leaves the builder to Administration. */
  compact?: boolean
}

export function RoomWebhooks({ room, compact = false }: RoomWebhooksProps): ReactElement {
  const toast = useToast()
  const { data: webhooks, isLoading, error } = useWebhooks(room.id)
  const createWebhook = useCreateWebhook()
  const rotateWebhook = useRotateWebhook()
  const deleteWebhook = useDeleteWebhook()

  const [name, setName] = useState('')
  const [rate, setRate] = useState(String(DEFAULT_RATE))
  const [nameError, setNameError] = useState<string | null>(null)
  const [revealed, setRevealed] = useState<WebhookSecret | null>(null)
  const [rotating, setRotating] = useState<Webhook | null>(null)
  const [revoking, setRevoking] = useState<Webhook | null>(null)

  const create = (): void => {
    const trimmed = name.trim()
    if (trimmed === '') {
      setNameError('Give the webhook a name so you can tell it apart later.')
      return
    }
    setNameError(null)

    const parsed = Number.parseInt(rate, 10)
    createWebhook.mutate(
      {
        roomId: room.id,
        body: {
          name: trimmed,
          rate_limit_per_minute: Number.isFinite(parsed) ? parsed : DEFAULT_RATE,
        },
      },
      {
        onSuccess: (secret) => {
          setName('')
          setRate(String(DEFAULT_RATE))
          setRevealed(secret)
        },
        onError: (failure) => {
          if (isApiError(failure) && failure.code === 'forbidden') {
            toast.error('This server only lets moderators create webhooks in a room.')
            return
          }
          toast.error(describe(failure, 'The webhook could not be created.'))
        },
      },
    )
  }

  const confirmRotate = (): void => {
    const target = rotating
    if (target === null) return

    rotateWebhook.mutate(
      { roomId: room.id, webhookId: target.id },
      {
        onSuccess: (secret) => {
          setRotating(null)
          setRevealed(secret)
        },
        onError: (failure) =>
          toast.error(describe(failure, `The secret for ${target.name} could not be rotated.`)),
      },
    )
  }

  const confirmRevoke = (): void => {
    const target = revoking
    if (target === null) return

    deleteWebhook.mutate(
      { roomId: room.id, webhookId: target.id },
      {
        onSuccess: () => {
          toast.success(`${target.name} was revoked.`)
          setRevoking(null)
        },
        onError: (failure) => toast.error(describe(failure, `${target.name} could not be revoked.`)),
      },
    )
  }

  if (error !== null) {
    return (
      <p className={styles.error} role="alert">
        {isApiError(error) && error.status === 403
          ? 'Webhooks in this room are managed by its moderators.'
          : describe(error, 'The webhooks could not be loaded.')}
      </p>
    )
  }

  return (
    <div className={`${styles.stack} ${compact ? styles.compact : ''}`}>
      <p className={styles.sectionHint}>
        {compact
          ? 'External systems can post messages to this channel.'
          : 'A webhook lets a build server, a monitor or a script post into this room over plain HTTP, without an account. Each one has its own URL, and that URL is the credential.'}
      </p>
      <a
        className={styles.docsLink}
        href="https://relaii.app/docs#incoming-webhooks"
        target="_blank"
        rel="noreferrer"
      >
        API documentation ↗
      </a>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Existing webhooks</h3>
        {isLoading || webhooks === undefined ? (
          <div className={styles.loading}>
            <Spinner label="Loading webhooks" />
          </div>
        ) : webhooks.length === 0 ? (
          <p className={styles.empty}>Nothing posts into this room yet.</p>
        ) : (
          <ul className={styles.list}>
            {webhooks.map((webhook) => (
              <WebhookRow
                key={webhook.id}
                webhook={webhook}
                roomId={room.id}
                onRotate={setRotating}
                onDelete={setRevoking}
              />
            ))}
          </ul>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>New webhook</h3>
        <div className={styles.form}>
          <TextField
            className={compact ? styles.compactField : undefined}
            label="Name"
            labelExtra={`${name.length}/80`}
            hint={compact ? undefined : 'Shown beside the messages it posts, so make it recognisable.'}
            value={name}
            error={nameError}
            maxLength={80}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setName(event.target.value)
              if (nameError !== null) setNameError(null)
            }}
          />
          <Select
            className={compact ? styles.compactField : undefined}
            label="Rate limit"
            hint={compact ? undefined : 'Messages this webhook may post each minute.'}
            value={rate}
            options={rateOptions(DEFAULT_RATE)}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => setRate(event.target.value)}
          />
          <div className={styles.actionsBar}>
            <Button
              size={compact ? 'sm' : 'md'}
              variant="primary"
              loading={createWebhook.isPending}
              onClick={create}
            >
              Create webhook
            </Button>
          </div>
        </div>
      </section>

      {compact ? null : (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Message builder</h3>
          <p className={styles.sectionHint}>
            Build a plain message or a small block layout, preview it, then copy the JSON or the complete request. Add an external_id to the copied payload when retries must be idempotent.
          </p>
          <WebhookMessageBuilder webhookUrl="<your webhook URL>" />
        </section>
      )}

      {/* The one moment the secret exists outside the server. */}
      <Modal
        open={revealed !== null}
        onClose={() => setRevealed(null)}
        title="Copy this URL now"
        size="md"
        closeOnOverlayClick={false}
        footer={
          <Button variant="primary" onClick={() => setRevealed(null)}>I have copied it</Button>
        }
      >
        <p className={styles.warning}>
          This is the only time this URL will be shown. The server keeps only a hash of it, so it
          cannot be looked up again. If it is lost, rotate the webhook — which issues a new URL and
          stops the old one working immediately.
        </p>
        {revealed === null ? null : (
          <div className={styles.reveal}>
            <CopyBlock label="Webhook URL" value={revealed.url} />
            <CopyBlock
              label="Try it"
              value={curlExample(revealed.url)}
              hint="Anyone holding this URL can post into the room, so treat it like a password."
            />
          </div>
        )}
      </Modal>

      <Modal
        open={rotating !== null}
        onClose={() => setRotating(null)}
        title="Rotate secret"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRotating(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={rotateWebhook.isPending} onClick={confirmRotate}>
              Rotate
            </Button>
          </>
        }
      >
        <p className={styles.sectionHint}>
          {rotating === null
            ? ''
            : `A new URL is issued for ${rotating.name} and the current one stops working at once. Anything still posting with the old URL will start failing until it is updated.`}
        </p>
      </Modal>

      <Modal
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title="Revoke webhook"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevoking(null)}>
              Cancel
            </Button>
            <Button variant="danger" loading={deleteWebhook.isPending} onClick={confirmRevoke}>
              Revoke
            </Button>
          </>
        }
      >
        <p className={styles.sectionHint}>
          {revoking === null
            ? ''
            : `${revoking.name} stops working immediately and cannot be restored. Messages it has already posted stay in the room.`}
        </p>
      </Modal>
    </div>
  )
}

export default RoomWebhooks
